import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X, Eye, EyeOff, Loader2, Check, ArrowLeft, Mail } from 'lucide-react';

type AuthTab = 'signin' | 'signup' | 'forgot';

const AuthModal: React.FC = () => {
  const { showAuthModal, setShowAuthModal, signIn, signUp, resetPassword } = useAuth();
  const [tab, setTab] = useState<AuthTab>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!showAuthModal) return null;

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  const switchTab = (newTab: AuthTab) => {
    setTab(newTab);
    resetForm();
  };

  const handleClose = () => {
    setShowAuthModal(false);
    resetForm();
    setTab('signin');
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email.trim() || !password.trim()) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    const { error: err } = await signIn(email, password);
    setLoading(false);

    if (err) {
      setError(err);
    } else {
      handleClose();
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email.trim() || !password.trim() || !firstName.trim() || !lastName.trim()) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const { error: err } = await signUp(email, password, firstName, lastName);
    setLoading(false);

    if (err) {
      setError(err);
    } else {
      setSuccess('Account created successfully. Please check your email to verify your account, then sign in.');
      setTimeout(() => {
        switchTab('signin');
      }, 3000);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    const { error: err } = await resetPassword(email.trim());
    setLoading(false);

    if (err) {
      setError(err);
    } else {
      setSuccess(
        'If an account exists for that email, a password reset link has been sent. Please check your inbox (and spam folder).'
      );
    }
  };

  const inputClass =
    'w-full bg-white border border-[#E8DFD0] px-4 py-3.5 font-sans text-sm text-[#2C2C2C] placeholder:text-[#bbb] focus:outline-none focus:border-[#C9A961] transition-colors';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-[#FAF8F3] w-full max-w-md relative animate-fade-in overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-5 right-5 z-10 w-8 h-8 flex items-center justify-center text-[#999] hover:text-[#2C2C2C] transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center pt-10 pb-6 px-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-[1px] bg-[#C9A961]" />
            <span className="font-serif text-xl tracking-[0.3em] font-light text-[#1A3B5C]">ZERA</span>
            <div className="w-8 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-[#1A3B5C] font-light mb-2">
            {tab === 'signin' && 'Welcome Back'}
            {tab === 'signup' && 'Join Zera'}
            {tab === 'forgot' && 'Reset Password'}
          </h2>
          <p className="font-sans text-xs text-[#999]">
            {tab === 'signin' && 'Sign in to access your saved journeys and preferences'}
            {tab === 'signup' && 'Create your account to begin your extraordinary journey'}
            {tab === 'forgot' && 'Enter your email and we will send you a secure link to reset your password'}
          </p>
        </div>

        {/* Tab Switcher (hidden on forgot view) */}
        {tab !== 'forgot' && (
          <div className="flex mx-8 mb-6 border border-[#E8DFD0]">
            <button
              onClick={() => switchTab('signin')}
              className={`flex-1 py-3 font-sans text-[10px] tracking-[0.2em] uppercase transition-all duration-300 ${
                tab === 'signin'
                  ? 'bg-[#1A3B5C] text-white'
                  : 'bg-transparent text-[#999] hover:text-[#1A3B5C]'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => switchTab('signup')}
              className={`flex-1 py-3 font-sans text-[10px] tracking-[0.2em] uppercase transition-all duration-300 ${
                tab === 'signup'
                  ? 'bg-[#1A3B5C] text-white'
                  : 'bg-transparent text-[#999] hover:text-[#1A3B5C]'
              }`}
            >
              Create Account
            </button>
          </div>
        )}

        {/* Error / Success Messages */}
        {error && (
          <div className="mx-8 mb-4 p-3 bg-red-50 border border-red-200 text-red-600 font-sans text-xs">
            {error}
          </div>
        )}
        {success && (
          <div className="mx-8 mb-4 p-3 bg-green-50 border border-green-200 text-green-700 font-sans text-xs flex items-start gap-2">
            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        {/* Sign In Form */}
        {tab === 'signin' && (
          <form onSubmit={handleSignIn} className="px-8 pb-10 space-y-4">
            <div>
              <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="your@email.com"
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999]">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => switchTab('forgot')}
                  className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#C9A961] hover:text-[#b8944d] transition-colors"
                >
                  Forgot?
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-12`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#bbb] hover:text-[#666] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#C9A961] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#b8944d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>

            <p className="text-center font-sans text-xs text-[#999] mt-4">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => switchTab('signup')}
                className="text-[#C9A961] hover:text-[#b8944d] transition-colors underline"
              >
                Create one
              </button>
            </p>
          </form>
        )}

        {/* Sign Up Form */}
        {tab === 'signup' && (
          <form onSubmit={handleSignUp} className="px-8 pb-10 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                  First Name *
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass}
                  placeholder="Alexandra"
                />
              </div>
              <div>
                <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass}
                  placeholder="Whitfield"
                />
              </div>
            </div>
            <div>
              <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="your@email.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-12`}
                  placeholder="Min. 6 characters"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#bbb] hover:text-[#666] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="font-sans text-[10px] text-[#bbb] mt-1">Minimum 6 characters</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#C9A961] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#b8944d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>

            <p className="text-center font-sans text-xs text-[#999] mt-4">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchTab('signin')}
                className="text-[#C9A961] hover:text-[#b8944d] transition-colors underline"
              >
                Sign in
              </button>
            </p>
          </form>
        )}

        {/* Forgot Password Form */}
        {tab === 'forgot' && (
          <form onSubmit={handleForgot} className="px-8 pb-10 space-y-4">
            <div>
              <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#bbb]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${inputClass} pl-11`}
                  placeholder="your@email.com"
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <p className="font-sans text-[10px] text-[#bbb] mt-2 leading-relaxed">
                We'll email you a secure link to choose a new password. The link expires in 1 hour.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !!success}
              className="w-full py-4 bg-[#C9A961] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#b8944d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                </>
              ) : success ? (
                'Email Sent'
              ) : (
                'Send Reset Link'
              )}
            </button>

            <button
              type="button"
              onClick={() => switchTab('signin')}
              className="w-full flex items-center justify-center gap-2 font-sans text-xs text-[#999] hover:text-[#1A3B5C] transition-colors mt-4"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Sign In
            </button>
          </form>
        )}

        {/* Decorative bottom */}
        <div className="h-1 bg-gradient-to-r from-[#1A3B5C] via-[#C9A961] to-[#1A3B5C]" />
      </div>
    </div>
  );
};

export default AuthModal;
